/**
 * Auditions filter config (listing-filters convergence).
 *
 * Unlike organizations/dancers, the auditions listing is served by a bespoke
 * cached loader (`get-cached-auditions-listing`) with its own two-query union +
 * upcoming-date predicate + in-memory match ranking — it does NOT go through
 * `applyListingFilters`. So auditions does not define a DB-facing `defineListing`
 * registry (its facets don't map to a single column/predicate the shared query
 * builder would run). Instead this module builds the UI-only `ListingFilterConfig`
 * directly for <ListingFilterBar>. The loader keeps reading the same URL params
 * (`auditionType`, `gender`, `sort`, `q`) that the bar writes.
 *
 * Competitions reuse the same shape (shared `auditions` table) minus the org
 * slot + match toggle.
 */
import type { ListingFilterConfig } from '~/components/listing-filters/types';

type Translate = (key: string, defaultValue?: string) => string;

export interface AuditionsFilterConfigOptions {
  /** Include the "Best match" sort (only meaningful for a signed-in dancer). */
  includeBestMatch?: boolean;
}

export function auditionsFilterConfig(
  t: Translate,
  options: AuditionsFilterConfigOptions = {},
): ListingFilterConfig {
  return {
    search: {
      placeholder: t('auditions:searchPlaceholder', 'Search auditions…'),
    },
    facets: [
      {
        kind: 'dropdown',
        key: 'auditionType',
        label: t('auditions:filters.auditionType', 'Audition type'),
        options: [
          {
            value: 'public',
            label: t('auditions:filters.typePublic', 'Public'),
          },
          {
            value: 'private',
            label: t('auditions:filters.typePrivate', 'Private'),
          },
        ],
      },
      {
        kind: 'dropdown',
        key: 'gender',
        label: t('auditions:filters.gender', 'Gender'),
        options: [
          {
            value: 'female',
            label: t('auditions:filters.genderFemale', 'Female'),
          },
          { value: 'male', label: t('auditions:filters.genderMale', 'Male') },
        ],
      },
    ],
    sorts: [
      ...(options.includeBestMatch
        ? [
            {
              value: 'best',
              label: t('auditions:filters.sortBestMatch', 'Best match'),
            },
          ]
        : []),
      { value: 'newest', label: t('auditions:filters.sortNewest', 'Newest') },
      {
        value: 'nearest',
        label: t('auditions:filters.sortNearest', 'Nearest date'),
      },
    ],
    defaultSort: options.includeBestMatch ? 'best' : 'nearest',
  };
}
